/**
 * hooks/useBriefBuilder.ts — State management for the Brief Builder module.
 *
 * Unlike the job-polling hooks (useReports etc.), this hook manages a multi-step
 * interactive session with explicit stage transitions.
 */
import { useCallback, useState } from 'react'
import { briefBuilder, widgetLibrary } from '@/api/client'
import type {
  BriefSession,
  BriefSessionSummary,
  CreateSessionPayload,
  DashboardDraft,
  ModuleType,
  SubmitClarificationsPayload,
  SubmitModuleConfigPayload,
  UpdateStructurePayload,
  WidgetDefinition,
  ExportResponse,
} from '@/types/brief-builder'

export type BriefView =
  | { view: 'list' }
  | { view: 'new' }
  | { view: 'session'; session: BriefSession }
  | { view: 'busy'; message: string; session?: BriefSession }

export function useBriefBuilder() {
  const [briefView, setBriefView] = useState<BriefView>({ view: 'new' })
  const [sessions,  setSessions]  = useState<BriefSessionSummary[]>([])
  const [widgets,   setWidgets]   = useState<WidgetDefinition[]>([])
  const [error,     setError]     = useState<string | null>(null)
  const [lastExport, setLastExport] = useState<ExportResponse | null>(null)

  const clearError = () => setError(null)

  // ── Widget library ─────────────────────────────────────────────────────────

  const loadWidgets = useCallback(async () => {
    try {
      const data = await widgetLibrary.list()
      setWidgets(data)
    } catch (err) {
      // Non-blocking — widgets are a secondary resource
      console.warn('Failed to load widget library:', err)
    }
  }, [])

  // ── Session list ───────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    try {
      const data = await briefBuilder.listSessions()
      setSessions(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions.')
    }
  }, [])

  // ── Create session (intake + AI interpretation) ────────────────────────────

  const createSession = useCallback(async (payload: CreateSessionPayload) => {
    clearError()
    setBriefView({ view: 'busy', message: 'Running AI interpretation…' })
    try {
      const session = await briefBuilder.createSession(payload)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session.')
      setBriefView({ view: 'new' })
    }
  }, [])

  // ── Load existing session ──────────────────────────────────────────────────

  const openSession = useCallback(async (sessionId: string) => {
    clearError()
    try {
      const session = await briefBuilder.getSession(sessionId)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session.')
    }
  }, [])

  // ── Submit clarification answers → triggers recommendation ────────────────

  const submitClarifications = useCallback(async (
    sessionId: string,
    answers: Record<string, string>,
  ) => {
    clearError()
    setBriefView(prev =>
      prev.view === 'session'
        ? { view: 'busy', message: 'Generating recommendations…', session: prev.session }
        : { view: 'busy', message: 'Generating recommendations…' }
    )
    try {
      const session = await briefBuilder.submitClarifications(sessionId, { answers })
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recommendation failed.')
      setBriefView({ view: 'new' })
    }
  }, [])

  // ── Save human-edited structure ────────────────────────────────────────────

  const saveStructure = useCallback(async (
    sessionId: string,
    payload: UpdateStructurePayload,
  ) => {
    clearError()
    try {
      const session = await briefBuilder.updateStructure(sessionId, payload)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save structure.')
    }
  }, [])

  // ── Validate ───────────────────────────────────────────────────────────────

  const validateSession = useCallback(async (sessionId: string) => {
    clearError()
    setBriefView(prev =>
      prev.view === 'session'
        ? { view: 'busy', message: 'Validating…', session: prev.session }
        : { view: 'busy', message: 'Validating…' }
    )
    try {
      const session = await briefBuilder.validate(sessionId)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed.')
    }
  }, [])

  // ── Configure AI modules ───────────────────────────────────────────────────

  const submitModuleConfig = useCallback(async (
    sessionId: string,
    payload: SubmitModuleConfigPayload,
  ) => {
    clearError()
    setBriefView(prev =>
      prev.view === 'session'
        ? { view: 'busy', message: 'Saving configuration…', session: prev.session }
        : { view: 'busy', message: 'Saving configuration…' }
    )
    try {
      const session = await briefBuilder.configure(sessionId, payload)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration.')
      const session = await briefBuilder.getSession(sessionId).catch(() => null)
      if (session) setBriefView({ view: 'session', session })
    }
  }, [])

  // ── Export ─────────────────────────────────────────────────────────────────

  const exportSession = useCallback(async (sessionId: string) => {
    clearError()
    setBriefView(prev =>
      prev.view === 'session'
        ? { view: 'busy', message: 'Generating YAML config…', session: prev.session }
        : { view: 'busy', message: 'Generating YAML config…' }
    )
    try {
      const result = await briefBuilder.export(sessionId)
      setLastExport(result)
      const session = await briefBuilder.getSession(sessionId)
      setBriefView({ view: 'session', session })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.')
    }
  }, [])

  // ── Delete ─────────────────────────────────────────────────────────────────

  const deleteSession = useCallback(async (sessionId: string) => {
    await briefBuilder.deleteSession(sessionId)
    setBriefView({ view: 'list' })
    await loadSessions()
  }, [loadSessions])

  // ── Navigation ─────────────────────────────────────────────────────────────

  const goToNew    = () => { clearError(); setBriefView({ view: 'new' })  }
  const goToList   = () => { clearError(); setBriefView({ view: 'list' }); loadSessions() }

  return {
    briefView,
    sessions,
    widgets,
    error,
    lastExport,
    clearError,
    loadWidgets,
    loadSessions,
    createSession,
    openSession,
    submitClarifications,
    saveStructure,
    validateSession,
    submitModuleConfig,
    exportSession,
    deleteSession,
    goToNew,
    goToList,
  }
}
